import React, { useEffect, useRef, useCallback, useState } from "react";
import styled, { keyframes } from "styled-components";
import Markdown from "react-markdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faRobot, faThumbsUp, faThumbsDown, faFileAlt,
  faPaperPlane, faDownload, faTimesCircle, faPlus, faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import evaApi from "../../services/api/evaApi";
import useAppSession from "../../stores/useAppSession.js";
import useChat from "../../stores/useChat.js";
import DocsModal from "../DocsModal/DocsModal.jsx";
import ModalConfirmExit from "../ModalConfirmExit/ModalConfirmExit";


const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;


const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: #f2f2f2;
  height: calc(100vh - 50px);
  box-sizing: border-box;
`;

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  background: transparent;
  box-sizing: border-box;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #dcdcdc; border-radius: 8px; }
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isUser ? "flex-end" : "flex-start"};
  animation: ${fadeIn} 0.3s ease-out;
  width: 100%;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.isUser ? "#666" : "#ff8f40"};
  flex-direction: ${props => props.isUser ? "row-reverse" : "row"};
`;

const MessageText = styled.div`
  background: #ffffff;
  color: #1d1d1f;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #eaeaea;
  font-size: 15px;
  line-height: 1.5;
  max-width: 85%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  position: relative;
  border-bottom-right-radius: ${props => props.isUser ? "2px" : "6px"};
  border-bottom-left-radius: ${props => props.isUser ? "6px" : "2px"};
`;


const TypingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 18px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  border-bottom-left-radius: 2px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  width: fit-content;

  span {
    width: 6px;
    height: 6px;
    background-color: #ff8f40;
    border-radius: 50%;
    display: inline-block;
    animation: ${blink} 1.4s infinite both;
  }
  span:nth-child(2) { animation-delay: 0.2s; }
  span:nth-child(3) { animation-delay: 0.4s; }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  opacity: 0.4;
  transition: opacity 0.2s;
  ${MessageWrapper}:hover & { opacity: 1; }
`;

const ActionBtnFeedback = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  color: ${props => props.active ? props.activeColor : "#999"};
  transition: all 0.2s;
  &:hover { color: ${props => props.activeColor || "#333"}; }
`;

const InputSection = styled.div`
  width: 100%;
  padding: 20px 0;
  background: transparent;
  box-sizing: border-box;
`;

const InputBoxContainer = styled.div`
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  box-sizing: border-box;
  width: 100%;
  &:focus-within { border-color: #ff8f40; }
`;

const TextArea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 4px;
  font-size: 15px;
  min-height: 40px;
  max-height: 180px;
  font-family: inherit;
  box-sizing: border-box;
  background: transparent;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f5f5f7;
`;

const IconButton = styled.button`
  background: ${props => props.primary ? "#ff8f40" : "transparent"};
  color: ${props => props.primary ? "#fff" : "#86868b"};
  border: none;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { background: ${props => props.primary ? "#e67e35" : "#f0f0f0"}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

function decodeEnvironmentId(token) {
  try {
    const decoded = jwtDecode(token);
    return decoded?.user_access?.environment?.environmentid || null;
  } catch { return null; }
}

const Chatbot = ({ onSessionId, onLogout }) => {
  const navigate = useNavigate();
  const { token, clientId, sessionId: storedSessionId } = useAppSession();
  const chat = useChat();

  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState({});
  const [docsModal, setDocsModal] = useState({ open: false, docs: null, message: null });
  const [showExitModal, setShowExitModal] = useState(false);
  const [loadingExit, setLoadingExit] = useState(false);

  const messagesEndRef = useRef(null);
  const textAreaRef = useRef(null);
  const hasRenderedRef = useRef(false);
  const inactiveTimerRef = useRef(null);
  const hasEndedConversationRef = useRef(false);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  // Ajuste devido a atualizacao do chrome
  //useEffect(scrollToBottom, [chat.messages, chat.botTyping, chat.currentBotMessage]);
  useEffect(() => { scrollToBottom(); }, [chat.messages, chat.botTyping, chat.currentBotMessage]);

  const inactivityLimit = chat.inactivityTimeout * 1000;

  const resetTimer = useCallback(() => {
    if (inactiveTimerRef.current) clearTimeout(inactiveTimerRef.current);
    inactiveTimerRef.current = setTimeout(() => confirmExit(), inactivityLimit);
  }, [inactivityLimit]);

  const confirmExit = () => {
    if (hasEndedConversationRef.current) return;
    hasEndedConversationRef.current = true;
    chat.setFimConversa(true);
    bye();
  };

  useEffect(() => {
    resetTimer();
    const evs = ["mousemove", "keydown", "click", "scroll"];
    evs.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(inactiveTimerRef.current);
      evs.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [resetTimer]);

  const handleRate = async (index, type, id) => {
    const rating = feedback[index] === type ? null : type;
    setFeedback({ ...feedback, [index]: rating });
    try {
      await evaApi.rate({ id, sessionid: chat.session, client_id: clientId, rating });
    } catch (e) { console.error("Erro Rate:", e); }
  };

  const handleOpenDocs = async (msg) => {
    try {
      const response = await evaApi.getDocs({
        environmentid: decodeEnvironmentId(token),
        id: msg.id,
        sessionid: chat.session
      });
      if (response?.data?.length > 0) {
        setDocsModal({ open: true, docs: response.data[0], message: msg });
      } else { toast.warn("Documentação não disponível."); }
    } catch (err) { console.error("Erro Docs:", err); }
  };

  const typeMessage = useCallback(async (message, id = null, animated = true) => {

    chat.setBotTyping(true);
    chat.setIsTyping(true);
    chat.setCurrentBotMessage("");

    if (animated) {
      const speed = 5;
      let currentText = "";
      for (let i = 0; i < message.length; i += speed) {
        currentText += message.slice(i, i + speed);
        chat.setCurrentBotMessage(currentText);
        await new Promise(r => setTimeout(r, 25));
      }
    } else {
      chat.setCurrentBotMessage(message);
      await new Promise(r => setTimeout(r, 100));
    }

    chat.addMessage({ sender: "bot", text: message, id });
    chat.setBotTyping(false);
    chat.setIsTyping(false);
    chat.setCurrentBotMessage("");
  }, [chat]);

  const handleSendMessage = async () => {
    if (chat.fimConversa || !input.trim() || chat.isTyping) return;

    const query = input;
    chat.addMessage({ sender: "user", text: query });
    setInput("");


    chat.setIsTyping(true);
    chat.setBotTyping(true);

    const body = {
      query: query,
      memory: "{}",
      searchdocs: chat.habilitaContexto,
      temperature: chat.temperature,
      template: chat.habilitaContexto ? "template_contexto" : "template_internet",
      client_id: clientId,
      email: chat.habilitaEmail,
      tem_perguntas_guiadas: chat.tem_perguntas_guiadas,
      zendesk: chat.habilitaZendesk,
      sessionid: chat.session,
      username: `WEB {{${clientId}}}`,
      cl: process.env.REACT_APP_CL,
      engine: chat.engine
    };

    try {
      const response = await evaApi.askai(body, token);

      await typeMessage(response.data.message, response.data.id);

      if (response.data.sessionid && !chat.session) {
        chat.setSession(response.data.sessionid);
        onSessionId(response.data.sessionid);
      }
      if (response.data.tem_perguntas_guiadas) chat.setTemPerguntasGuiadas(true);
      if (response.data.fim_conversa) chat.setFimConversa(true);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
      else toast.error("Erro na Engine de IA.");
      chat.setIsTyping(false);
      chat.setBotTyping(false);
    }
  };

  const bye = async () => {
    const body = {
      query: `Se despeça de maneira informal...`,
      memory: "{}",
      searchdocs: !chat.habilitaContexto,
      temperature: 0.1,
      template: !chat.habilitaContexto ? "template_contexto" : "template_internet",
      client_id: clientId,
      email: chat.habilitaEmail,
      zendesk: chat.habilitaZendesk,
      sessionid: chat.session,
      username: `WEB {{${clientId}}}`,
      cl: process.env.REACT_APP_CL,
      engine: chat.engine,
    };
    try {
      const res = await evaApi.askai(body, token);
      typeMessage(res.data.message, res.data.id);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (hasRenderedRef.current) return;
    hasRenderedRef.current = true;
    if (chat.session === "" && chat.shouldLog) {
      const startGrettings = async () => {
        chat.setShouldLog(false);
        const body = {
          query: "Se apresente de maneira informal...",
          memory: '{"role": "system", "content": "Você é um assistente virtual..."}',
          searchdocs: false,
          temperature: 0.9,
          template: "template_saudacao",
          client_id: clientId,
          email: false,
          zendesk: false,
          sessionid: "",
          username: `WEB {{${clientId}}}`,
          cl: process.env.REACT_APP_CL,
          engine: chat.engine,
        };
        try {
          const res = await evaApi.askai(body, token);
          typeMessage(res.data.message, res.data.id, false);
          chat.setSession(res.data.sessionid);
          onSessionId(res.data.sessionid);
        } catch (e) { if (e.response?.status === 401) onLogout(); }
      };
      startGrettings();
    }
  }, [clientId, token, chat, onSessionId, onLogout, typeMessage, chat.session]);

  const handleExportCSV = () => {
    const csv = "Sender,Text\n" + chat.messages.map(msg => `"${msg.sender}","${msg.text.replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `chat-${Date.now()}.csv`;
    link.click();
  };

  return (
    <PageWrapper>
      <ChatWrapper>
        <MessagesContainer>
          {chat.messages.map((msg, idx) => (
            <MessageWrapper key={idx} isUser={msg.sender === "user"}>
              <MessageHeader isUser={msg.sender === "user"}>
                <FontAwesomeIcon icon={msg.sender === "user" ? faUser : faRobot} />
                <span>{msg.sender === "user" ? "Você" : "EVA"}</span>
              </MessageHeader>
              <MessageText isUser={msg.sender === "user"}>
                <Markdown>{msg.text}</Markdown>
                {msg.sender === "bot" && idx !== 0 && (
                  <ActionGroup>
                    <ActionBtnFeedback onClick={() => handleRate(idx, 1, msg.id)} active={feedback[idx] === 1} activeColor="#ff8f40">
                      <FontAwesomeIcon icon={faThumbsUp} />
                    </ActionBtnFeedback>
                    <ActionBtnFeedback onClick={() => handleRate(idx, 0, msg.id)} active={feedback[idx] === 0} activeColor="#ff4d4f">
                      <FontAwesomeIcon icon={faThumbsDown} />
                    </ActionBtnFeedback>
                    <ActionBtnFeedback onClick={() => handleOpenDocs(msg)} active={true} activeColor="#333">
                      <FontAwesomeIcon icon={faFileAlt} />
                    </ActionBtnFeedback>
                  </ActionGroup>
                )}
              </MessageText>
            </MessageWrapper>
          ))}


          {(chat.botTyping || chat.isTyping) && !chat.currentBotMessage && (
            <MessageWrapper isUser={false}>
              <MessageHeader isUser={false}>
                <FontAwesomeIcon icon={faRobot} /> <span>EVA está pensando</span>
              </MessageHeader>
              <TypingBubble>
                <span /><span /><span />
              </TypingBubble>
            </MessageWrapper>
          )}

          {chat.currentBotMessage && (
            <MessageWrapper isUser={false}>
              <MessageHeader isUser={false}>
                <FontAwesomeIcon icon={faRobot} /> <span>EVA</span>
              </MessageHeader>
              <MessageText isUser={false}>
                <Markdown>{chat.currentBotMessage}</Markdown>
              </MessageText>
            </MessageWrapper>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputSection>
          {chat.fimConversa ? (
            <IconButton primary style={{ width: '100%', height: '50px' }} onClick={() => navigate(0)}>
              <FontAwesomeIcon icon={faPlus} /> &nbsp; Iniciar Nova Conversa
            </IconButton>
          ) : (
            <InputBoxContainer>
              <TextArea
                ref={textAreaRef}
                placeholder="Pergunte qualquer coisa ao assistente..."
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              />
              <ActionRow>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <IconButton onClick={handleExportCSV} title="Exportar CSV"><FontAwesomeIcon icon={faDownload} /></IconButton>
                  <IconButton onClick={() => setShowExitModal(true)} title="Encerrar Conversa"><FontAwesomeIcon icon={faTimesCircle} /></IconButton>
                </div>
                <IconButton primary disabled={!input.trim() || chat.isTyping} onClick={handleSendMessage}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                </IconButton>
              </ActionRow>
            </InputBoxContainer>
          )}
          <div style={{ fontSize: '11px', color: '#999', textAlign: 'center', marginTop: '12px' }}>
            EVA pode cometer erros. Verifique informações importantes.
          </div>
        </InputSection>
      </ChatWrapper>

      {docsModal.open && <DocsModal open={docsModal.open} onClose={() => setDocsModal({ open: false })} data={docsModal.docs} message={docsModal.message} />}
      <ModalConfirmExit open={showExitModal} onCancel={() => setShowExitModal(false)} onConfirm={() => { chat.setFimConversa(true); setShowExitModal(false); bye(); }} />
    </PageWrapper>
  );
};

export default Chatbot;
